import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, EmptyState, Field } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Megaphone, Link2, Video, CalendarClock, Users, Send, Mail, Loader2, Search, ArrowUpDown, MessageSquare, FileDown, Presentation, Trash2 } from "lucide-react";
import { EventThread } from "@/components/EventThread";
import { TypeQuestions, missingAnswer } from "@/components/TypeQuestions";

const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");

export default function Engagement() {
  const [meta, setMeta] = useState({ event_types: [], blast_audiences: [], disciplines: [], schools: [], role: null });
  useEffect(() => { api.get("/engagement-meta").then(({ data }) => setMeta(data)); }, []);

  // School Users get a focused "Request an Event" experience.
  if (meta.role === "School User") {
    return (
      <div data-testid="engagement-page">
        <PageHeader title="Request an Event" subtitle="Ask your Campus Coordinator to host an event with your school" />
        <SchoolRequestView meta={meta} />
      </div>
    );
  }

  return (
    <div data-testid="engagement-page">
      <PageHeader title="School Engagement" subtitle="Host events & run email outreach to schools and students" />
      <Tabs defaultValue="events">
        <TabsList className="mb-4">
          <TabsTrigger value="events" data-testid="tab-events"><Megaphone className="w-4 h-4 mr-1" />Events</TabsTrigger>
          <TabsTrigger value="blasts" data-testid="tab-blasts"><Mail className="w-4 h-4 mr-1" />Email Blasts</TabsTrigger>
        </TabsList>
        <TabsContent value="events"><EventsTab meta={meta} /></TabsContent>
        <TabsContent value="blasts"><BlastsTab meta={meta} /></TabsContent>
      </Tabs>
    </div>
  );
}

function EventsTab({ meta }) {
  const [rows, setRows] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ event_type: "", type_answers: {} });
  const [saving, setSaving] = useState(false);
  const [schedFor, setSchedFor] = useState(null);
  const [schedAt, setSchedAt] = useState("");
  const [schedContact, setSchedContact] = useState("");
  const [regsFor, setRegsFor] = useState(null);
  const [regs, setRegs] = useState([]);
  const [declineFor, setDeclineFor] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [schoolQuery, setSchoolQuery] = useState("");
  const [schoolScope, setSchoolScope] = useState("all");
  const [thread, setThread] = useState(null);
  const [slidesFor, setSlidesFor] = useState(null);
  const [uploading, setUploading] = useState(false);
  // list controls
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortKey, setSortKey] = useState("created");
  const [sortDir, setSortDir] = useState("desc");
  const schoolMatches = (meta.schools || []).filter((s) =>
    (schoolScope === "mine" ? s.assigned_cc_user_id === meta.me : true) &&
    `${s.name} ${s.state || ""}`.toLowerCase().includes(schoolQuery.trim().toLowerCase()));
  const selectedSchool = (meta.schools || []).find((s) => s.id === form.school_id);

  const load = () => api.get("/engagement-events").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  // Deep link from the dashboard: /engagement?event=<id> opens that event's thread.
  const focusId = new URLSearchParams(window.location.search).get("event");
  useEffect(() => {
    if (!focusId || !rows.length) return;
    const ev = rows.find((r) => r.id === focusId);
    if (ev) { setSearch(ev.title); setStatusFilter("all"); setThread(ev); }
  }, [focusId, rows.length]);

  const create = async () => {
    const missing = missingAnswer((meta.event_type_options || []).find((o) => o.label === form.event_type), form.type_answers);
    if (missing) return toast.error(`Please answer: ${missing}`);
    setSaving(true);
    try {
      await api.post("/engagement-events", form);
      toast.success("Event created — share the sign-up link");
      setCreateOpen(false); setForm({ event_type: (meta.event_types || [])[0] || "", type_answers: {} }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const copyLink = (ev) => { navigator.clipboard?.writeText(ev.signup_url); toast.success("Sign-up link copied"); };
  const doSchedule = async () => {
    try {
      if (schedFor.status === "Requested") {
        await api.post(`/engagement-events/${schedFor.id}/approve`);
      }
      const { data } = await api.post(`/engagement-events/${schedFor.id}/schedule`, { scheduled_at: schedAt, notify_contact_email: schedContact });
      toast.success(data.rescheduled ? `Rescheduled — ${data.emailed} notified & Outlook updated` : `Scheduled — Daily link created & ${data.emailed} notified`);
      setSchedFor(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openSchedule = (ev) => { setSchedFor(ev); setSchedAt(ev.scheduled_at || ""); setSchedContact(ev.notify_contact_email || ""); };
  const cancelEvent = async (ev) => {
    if (!window.confirm(`Cancel "${ev.title}"? Registrants${ev.notify_contact_email ? " and the school contact" : ""} will be notified and the Outlook invite removed.`)) return;
    try { const { data } = await api.post(`/engagement-events/${ev.id}/cancel`); toast.success("Event cancelled"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openRegs = async (ev) => {
    setRegsFor(ev);
    const { data } = await api.get(`/engagement-events/${ev.id}/registrations`); setRegs(data);
  };
  const approveEvent = async (ev) => {
    try { await api.post(`/engagement-events/${ev.id}/approve`); toast.success("Approved — school notified"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const uploadSlides = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/engagement-events/${slidesFor.id}/slides`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.get("/engagement-events");
      setRows(data);
      setSlidesFor(data.find((x) => x.id === slidesFor.id) || null);
      toast.success("File attached");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const removeSlide = async (i) => {
    try {
      await api.delete(`/engagement-events/${slidesFor.id}/slides/${i}`);
      const { data } = await api.get("/engagement-events");
      setRows(data);
      setSlidesFor(data.find((x) => x.id === slidesFor.id) || null);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const doDecline = async () => {
    try { await api.post(`/engagement-events/${declineFor.id}/decline`, { reason: declineReason }); toast.success("Declined — school notified"); setDeclineFor(null); setDeclineReason(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const s = search.trim().toLowerCase();
  const view = rows.filter((ev) => {
    if (statusFilter !== "all" && ev.status !== statusFilter) return false;
    if (typeFilter !== "all" && ev.event_type !== typeFilter) return false;
    if (!s) return true;
    return [ev.title, ev.school_name, ev.location, ev.event_type].filter(Boolean).join(" ").toLowerCase().includes(s);
  }).sort((a, b) => {
    let av, bv;
    if (sortKey === "title") { av = (a.title || "").toLowerCase(); bv = (b.title || "").toLowerCase(); }
    else if (sortKey === "school") { av = (a.school_name || "").toLowerCase(); bv = (b.school_name || "").toLowerCase(); }
    else if (sortKey === "when") { av = a.scheduled_at || ""; bv = b.scheduled_at || ""; }
    else if (sortKey === "signups") { av = a.registration_count || 0; bv = b.registration_count || 0; }
    else { av = a.created_at || ""; bv = b.created_at || ""; }
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });
  const toggleSort = (key) => { if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc"); else { setSortKey(key); setSortDir("asc"); } };
  const SortTh = ({ label, k }) => (
    <th className="text-left px-5 py-3"><button className="inline-flex items-center gap-1 hover:text-cc-blue" onClick={() => toggleSort(k)} data-testid={`sort-${k}`}>{label}<ArrowUpDown className="w-3 h-3" /></button></th>
  );

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search events, school, location…" className="pl-9" data-testid="event-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="event-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="Requested">Requested</SelectItem>
            <SelectItem value="Draft">Draft</SelectItem>
            <SelectItem value="Scheduled">Scheduled</SelectItem>
            <SelectItem value="Declined">Declined</SelectItem>
            <SelectItem value="Cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-44" data-testid="event-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {meta.event_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={() => setCreateOpen(true)} className="bg-cc-blue text-white ml-auto" data-testid="new-event-btn"><Megaphone className="w-4 h-4 mr-1" />New Event</Button>
      </div>
      <p className="text-xs text-zinc-400 mb-2" data-testid="event-count">{view.length} of {rows.length} events</p>
      <Card className="overflow-hidden">
        {view.length === 0 ? <EmptyState text="No events match. Create one and share its sign-up link." /> : (
          <div className="overflow-x-auto cc-scroll -m-5">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase">
                <tr>
                  <SortTh label="Event" k="title" /><SortTh label="School" k="school" />
                  <SortTh label="When" k="when" /><th className="text-left px-5 py-3">Status</th>
                  <SortTh label="Signups" k="signups" /><th className="text-left px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {view.map((ev) => (
                  <tr key={ev.id} className="border-t align-top" data-testid={`event-${ev.id}`}>
                    <td className="px-5 py-3 font-medium text-zinc-800">{ev.title}<div className="text-xs text-zinc-400">{ev.event_type}{ev.location ? ` · ${ev.location}` : ""}</div>{ev.requested_by_school && <div className="text-xs text-amber-600" data-testid={`event-requested-${ev.id}`}>Requested by school</div>}{(ev.requester_name || ev.requester_email) && <div className="text-xs text-zinc-500" data-testid={`event-requester-${ev.id}`}>{[ev.requester_name, ev.requester_title, ev.requester_discipline, ev.requester_email].filter(Boolean).join(" · ")}</div>}{Object.keys(ev.type_answers || {}).length > 0 && <div className="text-xs text-cc-blue" data-testid={`event-answers-${ev.id}`}>{Object.values(ev.type_answers).join(" · ")}</div>}{(ev.students_attending != null || ev.staff_attending != null) && <div className="text-xs text-cc-blue" data-testid={`event-attendance-${ev.id}`}>{ev.students_attending || 0} students · {ev.staff_attending || 0} staff</div>}</td>
                    <td className="px-5 py-3 text-zinc-600">{ev.school_name || "—"}{ev.school_state && <span className="text-xs text-zinc-400"> · {ev.school_state}</span>}</td>
                    <td className="px-5 py-3 font-mono text-xs">{fmt(ev.scheduled_at)}</td>
                    <td className="px-5 py-3"><StatusBadge status={ev.status} /></td>
                    <td className="px-5 py-3">
                      <button className="inline-flex items-center gap-1 text-cc-blue text-xs" onClick={() => openRegs(ev)} data-testid={`view-regs-${ev.id}`}><Users className="w-3 h-3" />{ev.registration_count}</button>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-1">
                        {ev.status === "Requested" && <Button size="sm" className="h-7 text-xs bg-cc-green hover:bg-green-600 text-white" onClick={() => approveEvent(ev)} data-testid={`approve-event-${ev.id}`}>Approve</Button>}
                        {ev.status === "Requested" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openSchedule(ev)} data-testid={`approve-schedule-event-${ev.id}`}><CalendarClock className="w-3 h-3 mr-1" />Approve &amp; Schedule</Button>}
                        {ev.status === "Requested" && <Button size="sm" variant="outline" className="h-7 text-xs text-red-600" onClick={() => { setDeclineFor(ev); setDeclineReason(""); }} data-testid={`decline-event-${ev.id}`}>Decline</Button>}
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setThread(ev)} data-testid={`message-requester-${ev.id}`}><MessageSquare className="w-3 h-3 mr-1" />Message{ev.message_count ? ` (${ev.message_count})` : ""}</Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setSlidesFor(ev)} data-testid={`slides-event-${ev.id}`}><Presentation className="w-3 h-3 mr-1" />Slides{(ev.slides || []).length ? ` (${ev.slides.length})` : ""}</Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => copyLink(ev)} data-testid={`copy-link-${ev.id}`}><Link2 className="w-3 h-3 mr-1" />Sign-up link</Button>
                        {ev.status !== "Cancelled" && ev.status !== "Requested" && ev.status !== "Declined" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openSchedule(ev)} data-testid={`schedule-event-${ev.id}`}><CalendarClock className="w-3 h-3 mr-1" />{ev.status === "Scheduled" ? "Reschedule" : "Schedule"}</Button>}
                        {ev.daily_room_url && <a href={ev.daily_room_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-7 text-xs"><Video className="w-3 h-3 mr-1" />Join</Button></a>}
                        {ev.status !== "Cancelled" && ev.status !== "Declined" && <Button size="sm" variant="outline" className="h-7 text-xs text-red-600" onClick={() => cancelEvent(ev)} data-testid={`cancel-event-${ev.id}`}>Cancel</Button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Create event */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New Engagement Event</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Title (optional)"><Input data-testid="event-title" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Leave blank to name it after the type & school" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Type">
                <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v, type_answers: {} })}>
                  <SelectTrigger data-testid="event-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{meta.event_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <TypeQuestions option={(meta.event_type_options || []).find((o) => o.label === form.event_type)} answers={form.type_answers} onChange={(a) => setForm({ ...form, type_answers: a })} testPrefix="event" />
            </div>
            <Field label="School (type a few letters)">
              <div className="flex items-center gap-2 mb-2">
                <Select value={schoolScope} onValueChange={setSchoolScope}>
                  <SelectTrigger className="w-40" data-testid="event-school-scope"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All schools</SelectItem><SelectItem value="mine">My schools</SelectItem></SelectContent>
                </Select>
                {selectedSchool && <span className="text-xs text-cc-blue" data-testid="event-school-selected">Selected: {selectedSchool.name}{selectedSchool.state ? ` (${selectedSchool.state})` : ""}</span>}
              </div>
              <Input data-testid="event-school-search" value={schoolQuery} onChange={(e) => setSchoolQuery(e.target.value)} placeholder="Search by school name or state…" />
              {schoolQuery.trim() && (
                <div className="mt-1 max-h-40 overflow-y-auto border rounded-md cc-scroll" data-testid="event-school-results">
                  {schoolMatches.length === 0 ? <p className="px-3 py-2 text-xs text-zinc-400">No schools match.</p> :
                    schoolMatches.slice(0, 25).map((s) => (
                      <button key={s.id} type="button" data-testid={`event-school-opt-${s.id}`} onClick={() => { setForm({ ...form, school_id: s.id }); setSchoolQuery(""); }}
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-zinc-50 flex justify-between">
                        <span>{s.name}</span><span className="text-xs text-zinc-400">{s.state}</span>
                      </button>
                    ))}
                </div>
              )}
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Students attending"><Input type="number" min="0" data-testid="event-students-attending" value={form.students_attending ?? ""} onChange={(e) => setForm({ ...form, students_attending: e.target.value === "" ? undefined : Number(e.target.value) })} /></Field>
              <Field label="School staff attending"><Input type="number" min="0" data-testid="event-staff-attending" value={form.staff_attending ?? ""} onChange={(e) => setForm({ ...form, staff_attending: e.target.value === "" ? undefined : Number(e.target.value) })} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date & time (optional)"><Input type="datetime-local" data-testid="event-datetime" value={form.scheduled_at || ""} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} /></Field>
            </div>
            <Field label="Description (optional)"><Textarea data-testid="event-description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
            <Field label="Notify school contact email (optional)"><Input type="email" data-testid="event-notify-contact" value={form.notify_contact_email || ""} onChange={(e) => setForm({ ...form, notify_contact_email: e.target.value })} placeholder="contact@school.edu — gets schedule/cancel alerts" /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving} className="bg-cc-blue text-white" data-testid="create-event-btn">Create Event</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule */}
      <Dialog open={!!schedFor} onOpenChange={(o) => !o && setSchedFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{schedFor?.status === "Requested" ? "Approve & Schedule" : schedFor?.status === "Scheduled" ? "Reschedule" : "Schedule"} "{schedFor?.title}"</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500 mb-2">{schedFor?.status === "Requested" ? "This approves the school's request and schedules it in one step. " : ""}{schedFor?.status === "Scheduled" ? "Updates the Outlook invite and re-emails everyone the new time." : "Creates a Daily.co virtual room, sends an Outlook invite, and emails"} all {schedFor?.registration_count || 0} registrant(s){schedContact ? " and the school contact" : ""} the join link.</p>
          <Field label="Date & time"><Input type="datetime-local" data-testid="event-sched-datetime" value={schedAt} onChange={(e) => setSchedAt(e.target.value)} /></Field>
          <Field label="Notify school contact email (optional)"><Input type="email" data-testid="event-sched-contact" value={schedContact} onChange={(e) => setSchedContact(e.target.value)} placeholder="contact@school.edu" /></Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSchedFor(null)}>Cancel</Button>
            <Button onClick={doSchedule} disabled={!schedAt} className="bg-cc-blue text-white" data-testid="confirm-schedule-event-btn">{schedFor?.status === "Requested" ? "Approve & Schedule" : schedFor?.status === "Scheduled" ? "Reschedule & notify" : "Schedule & notify"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Registrations */}
      <Dialog open={!!regsFor} onOpenChange={(o) => !o && setRegsFor(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto cc-scroll">
          <DialogHeader><DialogTitle>Registrations — {regsFor?.title}</DialogTitle></DialogHeader>
          {regs.length === 0 ? <div data-testid="regs-empty-state"><EmptyState text="No sign-ups yet. Share the sign-up link to start collecting RSVPs." /></div> : (
            <div className="space-y-2">
              {regs.map((r) => (
                <div key={r.id} className="border rounded-md p-3" data-testid={`reg-${r.id}`}>
                  <p className="font-medium text-sm text-zinc-800">{r.name} <span className="text-xs text-zinc-400">· {r.email}</span></p>
                  {(r.affiliation || r.notes) && <p className="text-xs text-zinc-500">{[r.affiliation, r.notes].filter(Boolean).join(" — ")}</p>}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Slides / handouts */}
      <Dialog open={!!slidesFor} onOpenChange={(o) => !o && setSlidesFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Slides &amp; handouts — {slidesFor?.title}</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500">Everyone who signed up gets a download link in the invite email, and the school sees it on their dashboard.</p>
          <div className="space-y-2 my-2" data-testid="slides-list">
            {(slidesFor?.slides || []).length === 0 ? <p className="text-sm text-zinc-400">Nothing attached yet.</p> :
              slidesFor.slides.map((sl, i) => (
                <div key={i} className="flex items-center justify-between border rounded-md px-3 py-2" data-testid={`slide-${i}`}>
                  <a href={sl.download_url} className="text-sm text-cc-blue inline-flex items-center gap-1 hover:underline"><FileDown className="w-3 h-3" />{sl.file_name}</a>
                  <button className="text-red-600" onClick={() => removeSlide(i)} data-testid={`slide-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
          </div>
          <input type="file" accept=".ppt,.pptx,.pdf,.doc,.docx" onChange={uploadSlides} disabled={uploading} data-testid="slides-upload-input" className="text-sm" />
          {uploading && <p className="text-xs text-zinc-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Uploading…</p>}
          <DialogFooter><Button variant="outline" onClick={() => setSlidesFor(null)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <EventThread event={thread} onClose={() => setThread(null)} onSent={load} />

      {/* Decline request */}
      <Dialog open={!!declineFor} onOpenChange={(o) => !o && setDeclineFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Decline "{declineFor?.title}"</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500 mb-2">The requesting school will be notified. A reason is optional but helpful.</p>
          <Field label="Reason (optional)"><Textarea data-testid="decline-reason" value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} placeholder="e.g. Timing conflicts with rotation schedule" /></Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineFor(null)}>Cancel</Button>
            <Button onClick={doDecline} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-decline-btn">Decline & notify</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SchoolRequestView({ meta }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ event_type: "", type_answers: {} });
  const [saving, setSaving] = useState(false);
  const [thread, setThread] = useState(null);
  const school = (meta.schools || [])[0] || {};
  const programs = school.programs || [];
  // Their details come straight off their account so they never retype them.
  useEffect(() => {
    setForm((f) => ({ ...f,
      requester_name: f.requester_name ?? meta.my_name ?? "",
      requester_email: f.requester_email ?? meta.my_email ?? "",
      requester_title: f.requester_title ?? meta.my_title ?? "" }));
  }, [meta.my_name, meta.my_email, meta.my_title]);
  const chosen = (meta.event_type_options || []).find((o) => o.label === form.event_type);
  const load = () => api.get("/engagement-events").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  // Deep link from the new-message banner: /engagement?event=<id> opens that event's thread.
  const focusId = new URLSearchParams(window.location.search).get("event");
  useEffect(() => {
    if (!focusId || thread) return;
    const ev = rows.find((r) => r.id === focusId);
    if (ev) setThread(ev);
  }, [rows, focusId]);

  const submit = async () => {
    const missing = missingAnswer(chosen, form.type_answers);
    if (missing) return toast.error(`Please answer: ${missing}`);
    setSaving(true);
    try {
      await api.post("/engagement-events", form);
      toast.success("Request sent — your Campus Coordinator will review it");
      setForm({ event_type: (meta.event_types || [])[0] || "", type_answers: {},
        requester_name: meta.my_name || "", requester_email: meta.my_email || "", requester_title: meta.my_title || "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card title="Request an Event">
        <div className="space-y-3">
          <p className="text-xs text-zinc-500" data-testid="req-school-context">Requesting for <b>{school.name || "your school"}</b>{school.state ? ` · ${school.state}` : ""} — your coordinator sees this with your request.</p>
          <Field label="Title (optional)"><Input data-testid="req-event-title" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Leave blank and we'll name it for you" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Your name"><Input data-testid="req-requester-name" value={form.requester_name || ""} onChange={(e) => setForm({ ...form, requester_name: e.target.value })} /></Field>
            <Field label="Your email"><Input type="email" data-testid="req-requester-email" value={form.requester_email || ""} onChange={(e) => setForm({ ...form, requester_email: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Your title"><Input data-testid="req-requester-title" value={form.requester_title || ""} onChange={(e) => setForm({ ...form, requester_title: e.target.value })} placeholder="e.g. Program Director" /></Field>
            <Field label="Program / discipline">
              <Select value={form.requester_discipline || ""} onValueChange={(v) => setForm({ ...form, requester_discipline: v })}>
                <SelectTrigger data-testid="req-requester-discipline"><SelectValue placeholder={programs.length ? "Which program is this for?" : "No programs on file"} /></SelectTrigger>
                <SelectContent>{(programs.length ? programs : meta.disciplines || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Type">
            <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v, type_answers: {} })}>
              <SelectTrigger data-testid="req-event-type"><SelectValue placeholder="Choose an event type" /></SelectTrigger>
              <SelectContent>{meta.event_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <TypeQuestions option={chosen} answers={form.type_answers} onChange={(a) => setForm({ ...form, type_answers: a })} testPrefix="req" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Students attending"><Input type="number" min="0" data-testid="req-students-attending" value={form.students_attending ?? ""} onChange={(e) => setForm({ ...form, students_attending: e.target.value === "" ? undefined : Number(e.target.value) })} /></Field>
            <Field label="School staff attending"><Input type="number" min="0" data-testid="req-staff-attending" value={form.staff_attending ?? ""} onChange={(e) => setForm({ ...form, staff_attending: e.target.value === "" ? undefined : Number(e.target.value) })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Preferred date & time (optional)"><Input type="datetime-local" data-testid="req-event-datetime" value={form.scheduled_at || ""} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} /></Field>
            <Field label="Contact email for updates (optional)"><Input type="email" data-testid="req-notify-contact" value={form.notify_contact_email || ""} onChange={(e) => setForm({ ...form, notify_contact_email: e.target.value })} placeholder="you@school.edu" /></Field>
          </div>
          <Field label="Description / details (optional)"><Textarea data-testid="req-event-description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <Button onClick={submit} disabled={saving} className="bg-cc-blue text-white" data-testid="submit-event-request-btn">{saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Sending…</> : <><Megaphone className="w-4 h-4 mr-1" />Send request</>}</Button>
        </div>
      </Card>

      <Card title="My Event Requests">
        {rows.length === 0 ? <EmptyState text="No event requests yet. Submit one and your coordinator will schedule it." /> : (
          <div className="space-y-2">
            {rows.map((ev) => (
              <div key={ev.id} className="border rounded-md p-3" data-testid={`req-event-${ev.id}`}>
                <div className="flex items-center justify-between">
                  <p className="font-medium text-sm text-zinc-800">{ev.title}</p>
                  <StatusBadge status={ev.status} />
                </div>
                <p className="text-xs text-zinc-500">{ev.event_type}{ev.scheduled_at ? ` · ${fmt(ev.scheduled_at)}` : " · date TBD"}{ev.location ? ` · ${ev.location}` : ""}</p>
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setThread(ev)} data-testid={`req-event-messages-${ev.id}`}>
                    <MessageSquare className="w-3 h-3 mr-1" />Messages{ev.message_count ? ` (${ev.message_count})` : ""}
                  </Button>
                  {ev.daily_room_url && <a href={ev.daily_room_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-7 text-xs"><Video className="w-3 h-3 mr-1" />Join</Button></a>}
                  {(ev.slides || []).map((sl, i) => (
                    <a key={i} href={sl.download_url} className="text-xs text-cc-blue inline-flex items-center gap-1 hover:underline" data-testid={`req-event-slide-${ev.id}-${i}`}><FileDown className="w-3 h-3" />{sl.file_name}</a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      <EventThread event={thread} onClose={() => setThread(null)} onSent={load} />
    </div>
  );
}


function BlastsTab({ meta }) {
  const [blasts, setBlasts] = useState([]);
  const [form, setForm] = useState({ audience: "", subject: "", body: "" });
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);

  const load = () => api.get("/email-blasts").then(({ data }) => setBlasts(data));
  useEffect(() => { load(); }, []);

  const needsDiscipline = form.audience === "Students by Discipline";
  const needsSchool = form.audience === "Specific School";

  const doPreview = async () => {
    if (!form.audience) return toast.error("Choose an audience first");
    const { data } = await api.post("/email-blasts/preview-recipients", form);
    setPreview(data);
  };
  const send = async () => {
    if (!form.audience || !form.subject.trim() || !form.body.trim()) return toast.error("Audience, subject and message are required");
    setSending(true);
    try {
      const { data } = await api.post("/email-blasts", form);
      toast.success(`Blast sent to ${data.recipient_count} recipient(s) (${data.sent_count} delivered)`);
      setForm({ audience: "", subject: "", body: "" }); setPreview(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card title="Compose Email Blast">
        <div className="space-y-3">
          <Field label="Audience">
            <Select value={form.audience} onValueChange={(v) => { setForm({ ...form, audience: v }); setPreview(null); }}>
              <SelectTrigger data-testid="blast-audience"><SelectValue placeholder="Select audience" /></SelectTrigger>
              <SelectContent>{meta.blast_audiences.map((aud) => <SelectItem key={aud} value={aud}>{aud}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {needsDiscipline && (
            <Field label="Discipline">
              <Select value={form.discipline} onValueChange={(v) => { setForm({ ...form, discipline: v }); setPreview(null); }}>
                <SelectTrigger data-testid="blast-discipline"><SelectValue placeholder="Select discipline" /></SelectTrigger>
                <SelectContent>{meta.disciplines.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
          {needsSchool && (
            <Field label="School">
              <Select value={form.school_id} onValueChange={(v) => { setForm({ ...form, school_id: v }); setPreview(null); }}>
                <SelectTrigger data-testid="blast-school"><SelectValue placeholder="Select school" /></SelectTrigger>
                <SelectContent>{meta.schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Subject"><Input data-testid="blast-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></Field>
          <Field label="Message (HTML allowed)"><Textarea rows={6} data-testid="blast-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="<p>Hi everyone, ...</p>" /></Field>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={doPreview} data-testid="preview-recipients-btn"><Users className="w-4 h-4 mr-1" />Preview recipients</Button>
            <Button onClick={send} disabled={sending} className="bg-cc-blue text-white" data-testid="send-blast-btn">{sending ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Sending…</> : <><Send className="w-4 h-4 mr-1" />Send blast</>}</Button>
          </div>
          {preview && (
            <div className="text-sm bg-zinc-50 border rounded-md p-3" data-testid="recipient-preview">
              <p className="font-medium text-zinc-700">{preview.count} recipient(s)</p>
              {preview.count > 0 && <p className="text-xs text-zinc-500 mt-1">{preview.sample.join(", ")}{preview.count > preview.sample.length ? " …" : ""}</p>}
            </div>
          )}
        </div>
      </Card>

      <Card title="Sent Blasts">
        {blasts.length === 0 ? <EmptyState text="No email blasts sent yet." /> : (
          <div className="space-y-2">
            {blasts.map((b) => (
              <div key={b.id} className="border rounded-md p-3" data-testid={`blast-${b.id}`}>
                <div className="flex items-center justify-between">
                  <p className="font-medium text-sm text-zinc-800">{b.subject}</p>
                  <span className="text-xs text-zinc-400 font-mono">{fmt(b.created_at)}</span>
                </div>
                <p className="text-xs text-zinc-500">{b.audience}{b.discipline ? ` · ${b.discipline}` : ""} — {b.sent_count}/{b.recipient_count} delivered · {b.created_by_name}</p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
